import {
  SelectComboField,
  type SelectComboFieldProps,
} from "components/Forms/SelectCombo/SelectCombo";
import { useRequiredSelectedCompanyId } from "hooks/useCompany";
import { useAssetPositionGroupsState } from "hooks/useScheme";
import {
  type AssetPositionGroup,
  useGetSchemeGroupsSchemeSchemeIdGroupsGet,
} from "kubb";
import { isNil } from "lodash-es";
import { assetPositionGroupListToOptions } from "./model";

type AssetPositionGroupSelectComboProps = Omit<
  SelectComboFieldProps,
  "options" | "error"
> &
  (
    | {
        /* optionally set the scheme for which groups should be displayed/loaded, otherwise uses selected schehem */
        useGivenSchemeId?: true;
        schemeId?: string | null;
      }
    | {
        useGivenSchemeId?: false;
      }
  );

function InnerGroupSelect(
  props: {
    loading: boolean;
    error?: Error | null;
    groups?: AssetPositionGroup[];
  } & Omit<SelectComboFieldProps, "options" | "error">,
) {
  const { disabled, loading, groups, error, ...rest } = props;
  const ready = !loading && !error;
  const haveGroups = groups && groups.length > 0;
  const altText: string = ready
    ? haveGroups
      ? "All"
      : "No Groups"
    : loading
      ? "Loading Groups"
      : error?.message || "Error Loading Groups";

  return (
    <SelectComboField
      {...rest}
      options={
        haveGroups
          ? [
              { label: "All", value: "undefined" },
              { label: "None", value: "NONE" },
              ...assetPositionGroupListToOptions(
                groups as AssetPositionGroup[],
                0,
              ),
            ]
          : []
      }
      placeholder={altText}
      disabled={!haveGroups || disabled}
    />
  );
}

function SelectedSchemeSelectCombo(
  props: Omit<SelectComboFieldProps, "options" | "error">,
) {
  const assetPositionGroups = useAssetPositionGroupsState();
  return (
    <InnerGroupSelect
      loading={assetPositionGroups.loading}
      error={assetPositionGroups.error}
      groups={assetPositionGroups.groups}
      {...props}
    />
  );
}

function GivenSchemeSelectCombo(
  props: AssetPositionGroupSelectComboProps & { schemeId: string },
) {
  const companyId = useRequiredSelectedCompanyId();
  const { schemeId, ...rest } = props;
  const { data, isLoading, error } = useGetSchemeGroupsSchemeSchemeIdGroupsGet(
    schemeId,
    {
      company_id: companyId,
    },
  );

  return (
    <InnerGroupSelect
      {...rest}
      groups={data?.data}
      error={error}
      loading={isLoading}
    />
  );
}

function AssetPositionGroupSelectCombo(
  props: AssetPositionGroupSelectComboProps,
) {
  const { useGivenSchemeId, ...rest } = props;
  if (useGivenSchemeId) {
    const { schemeId } = rest as Omit<SelectComboFieldProps, "options"> & {
      useGivenSchemeId?: true;
      schemeId?: string | null;
    };
    if (!isNil(schemeId)) {
      return <GivenSchemeSelectCombo {...rest} schemeId={schemeId} />;
    } else {
      return <>Placeholder</>;
    }
  } else {
    return <SelectedSchemeSelectCombo {...rest} />;
  }
}

export default AssetPositionGroupSelectCombo;
